import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { DashboardEntitlement } from "../lib/api";
import { isLocked } from "../lib/plans";
import { daysLeft } from "../lib/time";

// Shared card wrapper for the two actionable banner variants below
// (grace/payment-failed and locked/no-subscription) — same "card" chrome
// + flex layout, only the copy and CTA differ (C2).
function BannerCard({ children }: { children: ReactNode }) {
  return (
    <div
      className="card"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--space-3)",
        flexWrap: "wrap",
      }}
    >
      {children}
    </div>
  );
}

// Dashboard/Billing banner surfacing the account's billing state, in
// priority order (workstream C3): a payment failure in its grace window
// outranks everything (it's the one that needs action soonest); a locked
// account (never subscribed, or lapsed past its grace window) gets the
// trial CTA; otherwise an active trial gets a quiet countdown. An
// entitlement with none of these (a paying, non-trial account) renders
// nothing — there's nothing to say.
export function BillingBanner({
  entitlement,
}: {
  entitlement: DashboardEntitlement | null;
}) {
  if (!entitlement) return null;

  if (entitlement.grace_until) {
    return (
      <BannerCard>
        <p className="error" style={{ margin: 0 }}>
          <strong>Payment failed.</strong> Service continues until{" "}
          {entitlement.grace_until.slice(0, 10)} — update your payment
          method to avoid interruption.
        </p>
        <Link to="/billing" className="btn primary">
          Update payment
        </Link>
      </BannerCard>
    );
  }

  // entitlement is non-null here (the early return above already handled
  // that case) — isLocked(entitlement) always takes the 'locked' branch,
  // but using the shared helper keeps this check consistent with every
  // other locked-gate in the SPA.
  if (isLocked(entitlement)) {
    return (
      <BannerCard>
        <p style={{ margin: 0 }}>
          <strong>No active subscription.</strong> Start your 14-day free
          trial to unlock full financials + alerts.
        </p>
        <Link to="/welcome" className="btn primary">
          Start free trial
        </Link>
      </BannerCard>
    );
  }

  if (entitlement.trial_ends_at) {
    const n = daysLeft(entitlement.trial_ends_at);
    return (
      <div className="card">
        <p className="muted" style={{ margin: 0 }}>
          Free trial — {n} day{n === 1 ? "" : "s"} left.
        </p>
      </div>
    );
  }

  return null;
}
